// Renders an evidence-matrix bundle artifact (ROADMAP §5.1) inline in chat.
// The backend emits kind="xlsx" with metadata.artifact_subtype ===
// "evidence_matrix" + metadata.matrix_json (compact rows). We render the
// rows as a scannable table and surface the downloadable .xlsx export.

import { useMemo } from "react";

import type { Artifact } from "@/types/eryndal";
import { cn } from "@/utils/cn";

interface Props {
  artifact: Artifact;
  className?: string;
}

interface MatrixRow {
  id?: string | null;
  framework?: string | null;
  source?: string | null;
  title?: string | null;
  method?: string | null;
  owner?: string | null;
  refresh?: string | null;
}

const COLUMNS: { key: keyof MatrixRow; label: string }[] = [
  { key: "id", label: "Control" },
  { key: "framework", label: "Framework" },
  { key: "source", label: "Source" },
  { key: "title", label: "Title" },
  { key: "method", label: "Collection" },
  { key: "owner", label: "Owner" },
  { key: "refresh", label: "Refresh" },
];

function parseRows(raw: unknown): MatrixRow[] {
  if (typeof raw !== "string" || !raw.trim()) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as MatrixRow[]) : [];
  } catch {
    return [];
  }
}

export function EvidenceMatrix({ artifact, className }: Props) {
  const rows = useMemo(
    () => parseRows(artifact.metadata?.matrix_json),
    [artifact.metadata],
  );
  const downloadUrl =
    (artifact.metadata?.download_url as string | undefined) ?? "";

  // Group by framework so a mixed NIST + CCM matrix stays legible.
  const frameworks = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => set.add(r.framework ?? "—"));
    return Array.from(set);
  }, [rows]);

  return (
    <figure className={cn("panel-atelier overflow-hidden", className)}>
      <figcaption className="flex items-baseline justify-between gap-3 border-b border-ruleline px-5 py-2.5">
        <span className="flex items-baseline gap-3">
          <span className="label-mono">evidence matrix</span>
          {artifact.title ? (
            <span className="font-serif italic text-warmgray-500">{artifact.title}</span>
          ) : null}
        </span>
        {downloadUrl ? (
          <a
            href={downloadUrl}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 font-mono text-[0.7rem] text-oxblood-500 transition-colors duration-200 ease-atelier hover:text-oxblood-600"
          >
            download .xlsx ↓
          </a>
        ) : null}
      </figcaption>

      <div className="overflow-x-auto px-5 py-4">
        {rows.length === 0 ? (
          <p className="font-serif italic text-warmgray-400">
            Evidence matrix generated. Use the download link to open the full
            matrix (including the evidence requirement text) in Excel or
            Numbers.
          </p>
        ) : (
          <>
            <p className="mb-3 font-serif text-xs text-warmgray-400">
              {rows.length} control{rows.length === 1 ? "" : "s"} across{" "}
              {frameworks.join(", ")}. Full evidence-requirement text and
              source attribution are in the downloadable workbook.
            </p>
            <table className="min-w-full border-collapse overflow-hidden rounded-panel border border-ruleline font-serif text-sm">
              <thead>
                <tr className="border-b border-ruleline bg-parchment-100">
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className="border-r border-ruleline px-3 py-2 text-left font-mono text-[0.62rem] font-semibold uppercase tracking-[0.12em] text-warmgray-400 last:border-r-0"
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rIdx) => (
                  <tr
                    key={`${row.id ?? rIdx}-${rIdx}`}
                    className="border-b border-ruleline transition-colors duration-150 ease-atelier last:border-b-0 hover:bg-parchment-100"
                  >
                    {COLUMNS.map((col) => {
                      const value = row[col.key];
                      return (
                        <td
                          key={col.key}
                          className={cn(
                            "border-r border-ruleline px-3 py-2.5 leading-snug text-espresso-700 last:border-r-0",
                            col.key === "id" && "whitespace-nowrap font-mono text-xs text-oxblood-500",
                          )}
                        >
                          {value == null || value === "" ? (
                            <span className="text-warmgray-300">—</span>
                          ) : (
                            String(value)
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </figure>
  );
}
